import { FormControl } from '@mui/material';
import type { Decision } from '@shared/core/policy';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useMemo } from 'react';
import { AgCheckbox, AgInput, PolicyLockHint } from 'src/components/ui';
import { useFieldCommit } from 'src/hooks/useFieldCommit';
import { FeatureFlags } from 'src/lib/constants';

export default function CustomFieldsForm({
  customFieldsKey,
  customFieldValues,
  handleUpdateCustomFields,
  filterFields,
  canEdit = true,
  policyDecision,
}: {
  customFieldsKey: string;
  customFieldValues: Record<string, string | boolean>;
  handleUpdateCustomFields: (field: any, value: string | boolean) => void;
  filterFields?: (field: any) => boolean;
  canEdit?: boolean;
  policyDecision?: Decision;
}) {
  const flags = useFlags();

  const fields = useMemo(() => {
    let customFields = flags[FeatureFlags.CustomFields][customFieldsKey];

    if (filterFields) {
      customFields = customFields?.filter(filterFields);
    }

    return customFields ?? [];
  }, [flags, filterFields, customFieldsKey]);

  return (
    <>
      {fields?.map((field) => {
        const isEditable = policyDecision === undefined ? canEdit : policyDecision.allowed && canEdit;
        return (
          <PolicyLockHint key={field.key} decision={policyDecision}>
            <FieldByType
              disabled={!isEditable}
              name={field.name}
              type={field.type}
              value={customFieldValues[field.key]}
              onChange={(value) => handleUpdateCustomFields(field, value)}
            />
          </PolicyLockHint>
        );
      })}
    </>
  );
}

const FieldByType = ({
  type,
  name,
  value,
  onChange,
  disabled,
}: {
  type: string;
  name: string;
  value: string | boolean;
  onChange: (value: string | boolean) => void;
  disabled: boolean;
}) => {
  switch (type) {
    case 'boolean':
      return (
        <FormControl>
          <AgCheckbox
            isSelected={Boolean(value ?? false)}
            onChange={(isSelected) => onChange(isSelected)}
            isDisabled={disabled}
            label={name}
          />
        </FormControl>
      );
    default:
      return (
        <FormControl>
          <CustomTextField label={name} value={(value as string) ?? ''} onChange={onChange} disabled={disabled} />
        </FormControl>
      );
  }
};

/**
 * The One Law (`useFieldCommit`) on a custom text field: blur / Enter commit,
 * Escape reverts, Space stays a character. `onDraftChange` keeps the
 * per-keystroke `onChange` contract every host's own debounce relies on.
 */
const CustomTextField = ({
  label,
  value,
  onChange,
  disabled,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  disabled: boolean;
}) => {
  const field = useFieldCommit<string>({ value, onCommit: onChange, onDraftChange: onChange });

  return <AgInput label={label} isDisabled={disabled} {...field} />;
};
